import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Bot, X, Send, CheckCircle2, Circle, ArrowRight,
  Lightbulb, AlertTriangle, TrendingUp, Loader2,
  Sparkles, MessageCircle, BarChart3, Zap,
  ChefHat, Clock, ImageIcon, Share2, Calculator,
  ArrowUpRight, ArrowDownRight, Minus as MinusIcon
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const INSIGHT_ICONS = { warning: AlertTriangle, alert: AlertTriangle, action: TrendingUp, insight: Lightbulb };
const INSIGHT_COLORS = { warning: '#F59E0B', alert: '#EF4444', action: '#3B82F6', insight: '#10B981' };
const TIP_ICONS = { image: ImageIcon, plus: Zap, clock: Clock, share: Share2, chart: BarChart3, calculator: Calculator };
const REC_COLORS = { success: '#10B981', warning: '#F59E0B', insight: '#3B82F6', tip: '#8B5CF6' };

export default function AICopilot() {
  const { token, user } = useAuth();
  const { isAr } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('chat');
  const [checklist, setChecklist] = useState(null);
  const [insights, setInsights] = useState(null);
  const [summary, setSummary] = useState(null);
  const [combos, setCombos] = useState(null);
  const [contextTips, setContextTips] = useState([]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  const l = useCallback((ar, en) => isAr ? ar : en, [isAr]);
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Fetch all data when opened
  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API}/api/copilot/checklist`, { headers }).then(r => setChecklist(r.data)).catch(() => {});
    axios.get(`${API}/api/copilot/insights`, { headers }).then(r => setInsights(r.data)).catch(() => {});
    axios.get(`${API}/api/copilot/weekly-summary`, { headers }).then(r => setSummary(r.data?.summary)).catch(() => {});
    axios.get(`${API}/api/copilot/combo-suggestions`, { headers }).then(r => setCombos(r.data?.combos)).catch(() => {});
    axios.get(`${API}/api/copilot/contextual-tips?page=${location.pathname}`, { headers }).then(r => setContextTips(r.data?.tips || [])).catch(() => {});
  }, [open, token]);

  // Refresh contextual tips on page change
  useEffect(() => {
    if (!open || !token) return;
    axios.get(`${API}/api/copilot/contextual-tips?page=${location.pathname}`, { headers }).then(r => setContextTips(r.data?.tips || [])).catch(() => {});
  }, [location.pathname]);

  // Auto-greet
  useEffect(() => {
    if (open && !hasGreeted && messages.length === 0) {
      const name = user?.name || '';
      setMessages([{ role: 'assistant', content: isAr ? `أهلاً ${name}! أنا مساعد ذوق. كيف أقدر أساعدك اليوم؟` : `Hello ${name}! I'm Zouq Assistant. How can I help?` }]);
      setHasGreeted(true);
    }
  }, [open, hasGreeted, messages.length, user, isAr]);

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (tab === 'chat' && open) setTimeout(() => inputRef.current?.focus(), 100); }, [tab, open]);

  const sendMessage = async () => {
    if (!input.trim() || sending) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/api/copilot/chat`, { message: userMsg, context: location.pathname, history: messages.slice(-6) }, { headers });
      const msgObj = { role: 'assistant', content: data.response };
      if (data.action) msgObj.action = data.action;
      setMessages(prev => [...prev, msgObj]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: l('عذراً، حدث خطأ. حاول مرة أخرى.', 'Sorry, error occurred.') }]);
    }
    setSending(false);
  };

  const quickActions = [
    { ar: 'أضف صنف برجر واقيو سعره 45 ريال', en: 'Add Wagyu burger for 45 SAR' },
    { ar: 'اعمل خصم 20% على الكبسة', en: 'Create 20% discount on Kabsa' },
    { ar: 'كم طلب عندي اليوم؟', en: 'How many orders today?' },
    { ar: 'أوقف صنف الحمص', en: 'Disable Hummus item' },
  ];

  const completedCount = checklist?.completed || 0;
  const totalCount = checklist?.total || 6;
  const progress = checklist?.progress || 0;
  const hasInsights = (insights?.insights?.length > 0) || contextTips.length > 0;
  const hasSummary = summary && (summary.this_week?.orders > 0 || summary.last_week?.orders > 0);
  const hasCombos = combos && combos.length > 0;

  const tabItems = [
    { id: 'chat', ar: 'محادثة', en: 'Chat', icon: MessageCircle },
    { id: 'summary', ar: 'الملخص', en: 'Summary', icon: BarChart3 },
    { id: 'checklist', ar: 'المهام', en: 'Tasks', icon: CheckCircle2 },
    { id: 'insights', ar: 'رؤى', en: 'Insights', icon: Lightbulb },
  ];

  return (
    <>
      {/* Floating Button */}
      <button
        data-testid="copilot-toggle-btn"
        onClick={() => setOpen(o => !o)}
        className="fixed z-[60] transition-all duration-300 group"
        style={{
          bottom: 24, right: isAr ? 'auto' : 24, left: isAr ? 24 : 'auto',
          width: open ? 48 : 56, height: open ? 48 : 56,
          borderRadius: open ? 12 : 16,
          background: open ? 'rgba(120,113,108,0.2)' : 'linear-gradient(135deg, #D97706, #F59E0B)',
          boxShadow: open ? 'none' : '0 8px 32px rgba(217,119,6,0.35)',
          border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {open ? <X style={{ width: 20, height: 20, color: '#78716C' }} /> : (
          <>
            <Bot style={{ width: 26, height: 26, color: '#fff' }} />
            {(hasInsights || hasSummary) && <span style={{ position: 'absolute', top: -2, right: -2, width: 14, height: 14, borderRadius: '50%', background: '#EF4444', border: '2px solid #020617' }} />}
          </>
        )}
      </button>

      {/* Panel */}
      {open && (
        <div
          data-testid="copilot-panel"
          style={{
            position: 'fixed', bottom: 84, right: isAr ? 'auto' : 24, left: isAr ? 24 : 'auto',
            width: 400, maxWidth: 'calc(100vw - 48px)',
            height: 560, maxHeight: 'calc(100vh - 120px)',
            borderRadius: 20, background: '#0F0F14',
            border: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
            display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 59,
            animation: 'copilotSlideIn 0.25s ease-out',
            fontFamily: "'Tajawal', sans-serif", direction: isAr ? 'rtl' : 'ltr',
          }}
        >
          {/* Header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: 'linear-gradient(135deg, #D97706, #F59E0B)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Sparkles style={{ width: 18, height: 18, color: '#fff' }} />
            </div>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: '#F5F5F5', margin: 0 }}>{l('مساعد ذوق', 'Zouq Assistant')}</p>
              <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: 0 }}>{l('مستشارك الذكي', 'Your smart advisor')}</p>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0 8px' }}>
            {tabItems.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button key={t.id} data-testid={`copilot-tab-${t.id}`} onClick={() => setTab(t.id)} style={{
                  flex: 1, padding: '9px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                  fontSize: 11, fontWeight: active ? 600 : 400,
                  color: active ? '#F59E0B' : 'rgba(255,255,255,0.3)',
                  background: 'none', border: 'none',
                  borderBottom: active ? '2px solid #F59E0B' : '2px solid transparent',
                  cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'inherit',
                }}>
                  <Icon style={{ width: 13, height: 13 }} />
                  {l(t.ar, t.en)}
                  {t.id === 'checklist' && checklist && <span style={{ fontSize: 9, padding: '0 4px', borderRadius: 6, background: progress === 100 ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)', color: progress === 100 ? '#10B981' : '#F59E0B' }}>{completedCount}/{totalCount}</span>}
                  {t.id === 'summary' && hasSummary && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10B981' }} />}
                  {t.id === 'insights' && hasInsights && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#EF4444' }} />}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* ===== CHAT TAB ===== */}
            {tab === 'chat' && (
              <>
                <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {messages.map((msg, i) => (
                    <div key={i} data-testid={`copilot-msg-${msg.role}-${i}`} style={{
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '85%',
                    }}>
                      <div style={{
                        padding: '10px 14px',
                        borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                        background: msg.role === 'user' ? 'linear-gradient(135deg, #D97706, #B45309)' : 'rgba(255,255,255,0.05)',
                        color: msg.role === 'user' ? '#fff' : 'rgba(255,255,255,0.8)',
                        fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap',
                      }}>{msg.content}</div>
                      {/* Action Result Card */}
                      {msg.action && (
                        <ActionCard action={msg.action} l={l} onNavigate={(path) => { navigate(path); setOpen(false); }} />
                      )}
                    </div>
                  ))}
                  {sending && <div style={{ alignSelf: 'flex-start', padding: '10px 16px', borderRadius: '14px 14px 14px 4px', background: 'rgba(255,255,255,0.05)' }}><Loader2 style={{ width: 16, height: 16, color: '#F59E0B', animation: 'spin 1s linear infinite' }} /></div>}
                  <div ref={chatEndRef} />
                  {messages.filter(m => m.role === 'user').length === 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8 }}>
                      {quickActions.map((q, i) => (
                        <button key={i} data-testid={`copilot-quick-${i}`} onClick={() => setInput(l(q.ar, q.en))} style={{
                          padding: '8px 12px', borderRadius: 10, background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)',
                          fontSize: 12, cursor: 'pointer', textAlign: isAr ? 'right' : 'left', fontFamily: 'inherit', transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.target.style.background = 'rgba(217,119,6,0.1)'; e.target.style.color = '#F59E0B'; }}
                        onMouseLeave={e => { e.target.style.background = 'rgba(255,255,255,0.04)'; e.target.style.color = 'rgba(255,255,255,0.5)'; }}
                        >{l(q.ar, q.en)}</button>
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input ref={inputRef} data-testid="copilot-chat-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && sendMessage()} placeholder={l('اكتب سؤالك...', 'Type your question...')} style={{
                    flex: 1, padding: '10px 14px', borderRadius: 12,
                    border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.04)',
                    color: '#fff', fontSize: 13, outline: 'none', fontFamily: 'inherit',
                  }} />
                  <button data-testid="copilot-send-btn" onClick={sendMessage} disabled={!input.trim() || sending} style={{
                    width: 38, height: 38, borderRadius: 10,
                    background: input.trim() ? 'linear-gradient(135deg, #D97706, #F59E0B)' : 'rgba(255,255,255,0.05)',
                    border: 'none', cursor: input.trim() ? 'pointer' : 'default',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <Send style={{ width: 16, height: 16, color: input.trim() ? '#fff' : 'rgba(255,255,255,0.2)', transform: isAr ? 'scaleX(-1)' : 'none' }} />
                  </button>
                </div>
              </>
            )}

            {/* ===== SUMMARY TAB ===== */}
            {tab === 'summary' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
                {!summary ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <BarChart3 style={{ width: 32, height: 32, color: 'rgba(255,255,255,0.15)', margin: '0 auto 12px' }} />
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>{l('لا توجد بيانات كافية بعد.', 'Not enough data yet.')}</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {/* Revenue Card */}
                    <div style={{ padding: 14, borderRadius: 14, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: '0 0 8px' }}>{l('الإيرادات هذا الأسبوع', 'Revenue This Week')}</p>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span style={{ fontSize: 26, fontWeight: 800, color: '#F5F5F5' }}>{summary.this_week?.revenue?.toLocaleString()}</span>
                        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>{l('ر.س', 'SAR')}</span>
                        <ChangeIndicator value={summary.revenue_change_pct} />
                      </div>
                      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', margin: '4px 0 0' }}>
                        {l('الأسبوع الماضي:', 'Last week:')} {summary.last_week?.revenue?.toLocaleString()} {l('ر.س', 'SAR')}
                      </p>
                    </div>

                    {/* Orders + Avg Order */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <MiniStat label={l('الطلبات', 'Orders')} value={summary.this_week?.orders} change={summary.order_change_pct} />
                      <MiniStat label={l('متوسط الطلب', 'Avg Order')} value={`${summary.avg_order_value} ${l('ر.س', 'SAR')}`} />
                    </div>

                    {/* Top Item */}
                    {summary.top_item?.name && summary.top_item.name !== '—' && (
                      <div style={{ padding: 12, borderRadius: 12, background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.15)', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(217,119,6,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <ChefHat style={{ width: 18, height: 18, color: '#F59E0B' }} />
                        </div>
                        <div>
                          <p style={{ fontSize: 12, fontWeight: 600, color: '#F59E0B', margin: 0 }}>{l('نجم الأسبوع', 'Star of the Week')}</p>
                          <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', margin: 0 }}>{summary.top_item.name} ({summary.top_item.count} {l('طلب', 'orders')})</p>
                        </div>
                      </div>
                    )}

                    {/* Combo Suggestions */}
                    {hasCombos && (
                      <div style={{ padding: 12, borderRadius: 12, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)' }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: '#A78BFA', margin: '0 0 8px' }}>
                          <Zap style={{ width: 13, height: 13, display: 'inline', verticalAlign: -2 }} /> {l('اقتراحات كومبو', 'Combo Suggestions')}
                        </p>
                        {combos.map((c, i) => (
                          <div key={i} data-testid={`copilot-combo-${i}`} style={{
                            padding: '8px 0', borderTop: i > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                          }}>
                            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.65)', margin: 0 }}>
                              {c.items.join(' + ')} <span style={{ color: 'rgba(255,255,255,0.25)' }}>({c.ordered_together}x {l('معاً', 'together')})</span>
                            </p>
                            <p style={{ fontSize: 11, margin: '2px 0 0' }}>
                              <span style={{ textDecoration: 'line-through', color: 'rgba(255,255,255,0.25)' }}>{c.original_price} {l('ر.س', 'SAR')}</span>
                              <span style={{ color: '#A78BFA', fontWeight: 600, marginRight: 6, marginLeft: 6 }}>{c.suggested_price} {l('ر.س', 'SAR')}</span>
                              <span style={{ color: '#10B981', fontSize: 10 }}>(-{c.discount_pct}%)</span>
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recommendations */}
                    {summary.recommendations?.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.5)', margin: 0 }}>{l('توصيات', 'Recommendations')}</p>
                        {summary.recommendations.map((r, i) => (
                          <div key={i} data-testid={`copilot-rec-${i}`} style={{
                            padding: '8px 12px', borderRadius: 10,
                            background: `${REC_COLORS[r.type] || '#F59E0B'}08`,
                            border: `1px solid ${REC_COLORS[r.type] || '#F59E0B'}18`,
                          }}>
                            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>{l(r.ar, r.en)}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ===== CHECKLIST TAB ===== */}
            {tab === 'checklist' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#F5F5F5' }}>{l('تقدمك في الإعداد', 'Setup Progress')}</span>
                    <span style={{ fontSize: 11, color: progress === 100 ? '#10B981' : '#F59E0B', fontWeight: 600 }}>{progress}%</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.06)' }}>
                    <div style={{ height: '100%', borderRadius: 3, width: `${progress}%`, background: progress === 100 ? 'linear-gradient(90deg, #10B981, #34D399)' : 'linear-gradient(90deg, #D97706, #F59E0B)', transition: 'width 0.5s ease' }} />
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  {checklist?.items?.map(item => (
                    <button key={item.id} data-testid={`copilot-checklist-${item.id}`} onClick={() => { navigate(item.path); setOpen(false); }} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10,
                      background: item.completed ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${item.completed ? 'rgba(16,185,129,0.15)' : 'rgba(255,255,255,0.06)'}`,
                      cursor: 'pointer', textAlign: isAr ? 'right' : 'left', fontFamily: 'inherit', width: '100%', transition: 'all 0.15s',
                    }}>
                      {item.completed ? <CheckCircle2 style={{ width: 16, height: 16, color: '#10B981', flexShrink: 0 }} /> : <Circle style={{ width: 16, height: 16, color: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />}
                      <span style={{ flex: 1, fontSize: 12, color: item.completed ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.7)', textDecoration: item.completed ? 'line-through' : 'none' }}>{l(item.ar, item.en)}</span>
                      {!item.completed && <ArrowRight style={{ width: 12, height: 12, color: 'rgba(255,255,255,0.2)', transform: isAr ? 'scaleX(-1)' : 'none' }} />}
                    </button>
                  ))}
                </div>
                {progress === 100 && (
                  <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.15)', textAlign: 'center' }}>
                    <p style={{ fontSize: 12, color: '#10B981', fontWeight: 600 }}>{l('ممتاز! أكملت جميع المهام', 'Great! All tasks completed')}</p>
                  </div>
                )}
              </div>
            )}

            {/* ===== INSIGHTS TAB ===== */}
            {tab === 'insights' && (
              <div style={{ flex: 1, overflowY: 'auto', padding: 14 }}>
                {/* Contextual Tips */}
                {contextTips.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <p style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', margin: '0 0 6px' }}>{l('نصائح للصفحة الحالية', 'Tips for Current Page')}</p>
                    {contextTips.map((tip, i) => {
                      const TipIcon = TIP_ICONS[tip.icon] || Lightbulb;
                      return (
                        <button key={i} data-testid={`copilot-tip-${i}`} onClick={() => { if (tip.action_path) { navigate(tip.action_path); setOpen(false); } }} style={{
                          display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 10, width: '100%',
                          background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.12)',
                          cursor: tip.action_path ? 'pointer' : 'default', textAlign: isAr ? 'right' : 'left', fontFamily: 'inherit', marginBottom: 6,
                        }}>
                          <TipIcon style={{ width: 14, height: 14, color: '#A78BFA', flexShrink: 0, marginTop: 2 }} />
                          <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>{l(tip.ar, tip.en)}</p>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Business Insights */}
                {!insights?.insights?.length && !contextTips.length ? (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <Lightbulb style={{ width: 32, height: 32, color: 'rgba(255,255,255,0.15)', margin: '0 auto 12px' }} />
                    <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)' }}>{l('لا توجد رؤى حالياً.', 'No insights right now.')}</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {insights?.insights?.length > 0 && (
                      <p style={{ fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,0.4)', margin: '0 0 2px' }}>{l('تنبيهات الأعمال', 'Business Alerts')}</p>
                    )}
                    {insights?.insights?.map((ins, i) => {
                      const Icon = INSIGHT_ICONS[ins.type] || Lightbulb;
                      const color = INSIGHT_COLORS[ins.type] || '#F59E0B';
                      return (
                        <button key={i} data-testid={`copilot-insight-${i}`} onClick={() => { if (ins.action_path) { navigate(ins.action_path); setOpen(false); } }} style={{
                          display: 'flex', gap: 8, padding: '10px 12px', borderRadius: 10, width: '100%',
                          background: `${color}08`, border: `1px solid ${color}18`,
                          cursor: ins.action_path ? 'pointer' : 'default', textAlign: isAr ? 'right' : 'left', fontFamily: 'inherit',
                        }}>
                          <div style={{ width: 28, height: 28, borderRadius: 8, background: `${color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon style={{ width: 14, height: 14, color }} />
                          </div>
                          <p style={{ flex: 1, fontSize: 12, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, margin: 0 }}>{l(ins.ar, ins.en)}</p>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes copilotSlideIn { from { opacity: 0; transform: translateY(12px) scale(0.96); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}

/* ===== Sub-components ===== */

function ChangeIndicator({ value }) {
  if (value === undefined || value === null || value === 0) return <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', display: 'inline-flex', alignItems: 'center', gap: 2 }}><MinusIcon style={{ width: 10, height: 10 }} /> 0%</span>;
  const positive = value > 0;
  const color = positive ? '#10B981' : '#EF4444';
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return <span data-testid="change-indicator" style={{ fontSize: 11, color, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 2 }}><Icon style={{ width: 12, height: 12 }} /> {Math.abs(value)}%</span>;
}

function MiniStat({ label, value, change }) {
  return (
    <div style={{ padding: 12, borderRadius: 12, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', margin: '0 0 4px' }}>{label}</p>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 20, fontWeight: 700, color: '#F5F5F5' }}>{value}</span>
        {change !== undefined && <ChangeIndicator value={change} />}
      </div>
    </div>
  );
}

const ACTION_LABELS = {
  add_item: { ar: 'تم إضافة صنف', en: 'Item Added', icon: '🍽️' },
  update_price: { ar: 'تم تعديل السعر', en: 'Price Updated', icon: '💰' },
  toggle_item: { ar: 'تم تغيير الحالة', en: 'Status Changed', icon: '🔄' },
  create_offer: { ar: 'تم إنشاء عرض', en: 'Offer Created', icon: '🎁' },
  delete_item: { ar: 'تم حذف صنف', en: 'Item Deleted', icon: '🗑️' },
};

function ActionCard({ action, l, onNavigate }) {
  if (!action) return null;
  const success = action.success;
  const label = ACTION_LABELS[action.type] || { ar: 'تم', en: 'Done', icon: '✅' };
  const borderColor = success ? '#10B981' : '#EF4444';
  const bgColor = success ? 'rgba(16,185,129,0.08)' : 'rgba(239,68,68,0.08)';

  return (
    <div data-testid={`copilot-action-${action.type}`} style={{
      marginTop: 6, padding: '10px 12px', borderRadius: 12,
      background: bgColor, border: `1px solid ${borderColor}25`,
      display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 16 }}>{label.icon}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: borderColor }}>
          {success ? l(label.ar, label.en) : l('فشل التنفيذ', 'Action Failed')}
        </span>
      </div>
      <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', margin: 0, lineHeight: 1.5 }}>
        {action.message_ar || action.error || ''}
      </p>
      {success && action.navigate && (
        <button
          data-testid="copilot-action-navigate"
          onClick={() => onNavigate(action.navigate)}
          style={{
            alignSelf: 'flex-start', padding: '4px 12px', borderRadius: 8,
            background: `${borderColor}15`, border: `1px solid ${borderColor}30`,
            color: borderColor, fontSize: 11, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'inherit',
          }}
        >
          {l('عرض', 'View')} →
        </button>
      )}
    </div>
  );
}
